import { Position } from 'react-flow-renderer'
import { SessionView } from '../../session/Session'
import { RoleIsReadOnly } from '../../../components/role'

export type NodeHook = (arg: NodeAction[]) => void

export type NodeHooks = {
  isReadOnly: () => boolean
  openActionModal: NodeHook
  headlessAction: NodeHook
}

export type NodeData = {
  label?: string
  type?: string
  kind?: string
  path?: string
  src?: string
  rootAction?: boolean
  hasChildren?: boolean
  hasTarget?: Position
  hasSource?: Position
  lintErrors?: string[]
  actions?: NodeAction[]
  nodeHooks: NodeHooks
  nodeId?: string
  setNeedsAction?: (needsAction: boolean) => void
  setSelectedNode?: (node: GraphNode | null) => void
  setEvents?: (events: NodeEvent[]) => void
}

export type GraphEdge = {
  id: string
  source: string
  target: string
  animated?: boolean
  className?: string
  selectable?: boolean
  type: string
  data: NodeData
}

export type GraphNode = {
  id: string
  animated?: boolean
  className?: string
  selectable?: boolean
  type: string
  data: NodeData
  position: {
    x: number
    y: number
  }
}

export type NodeAction = {
  operation: string
  kind?: string
  path?: string
  value?: string
  nodeid?: string
  label?: string
  type?: string
  deletedData?: number[]
}

export type NodeEvent = {
  type: string
}

export type TreeNode = {
  label?: string
  path: string
  kind?: string
  type?: string
  children: TreeNode[]
  grouped_children: TreeNode[][]
  actions: NodeAction[]
  root_action: boolean
  line_start: number
  line_end: number
  lint_errors?: string[]
  node_id?: string
}

export type GraphResponse = {
  stream?: TreeNode[]
  resources?: TreeNode[]
  observability?: TreeNode[]
  has_undo: boolean
  has_redo: boolean
  view?: SessionView
}

export type Tree = {
  stream?: TreeNode[]
  resources?: TreeNode[]
  observability?: TreeNode[]
  read_only: boolean
  has_undo: boolean
  has_redo: boolean
}

export type RunEvent = {
  type: string
  content: string
}

export type RunResult = {
  input_events: {
    [key: string]: RunEvent[]
  }
  missing_inputs: {
    [key: string]: object
  }
  processor_events: {
    [key: string]: RunEvent[]
  }
  missing_processors: {
    [key: string]: object
  }
  output_events: {
    [key: string]: RunEvent[]
  }
  summary?: {
    input: number
    processor_errors: number
    output: number
  }
  error?: string
  force_closed?: boolean
}

type MockInput = {
  messages: string[]
}

type MockProcessor = {
  mapping: string
}

export type RunConfig = {
  enabled: boolean
  mock_inputs: {
    [key: string]: MockInput
  }
  mock_processors: {
    [key: string]: MockProcessor
  }
}

export type RunInfo = {
  config: RunConfig
  result?: RunResult
}

/**
 * Converts a GraphResponse object into a Tree object.
 *
 * @param {GraphResponse} res - The GraphResponse object to be converted.
 * @returns {Tree} The converted Tree object.
 */
export function GraphResToTree(res: GraphResponse): Tree {
  return {
    stream: res.stream,
    resources: res.resources,
    observability: res.observability,
    has_undo: res.has_undo,
    has_redo: res.has_redo,
    read_only: RoleIsReadOnly(res.view?.user_role || ''),
  }
}
